import React, { useState, useEffect, useReducer, useRef, useMemo } from "react";
import {
  Tabs,
  Tab,
  Grid,
  Box,
  Card,
  IconButton,
  Input,
  InputAdornment,
  Typography,
  Stack,
  Tooltip,
} from "@mui/material";
import dayjs from "dayjs";
import Button from "@mui/material/Button";
import FileDownloadOutlinedIcon from "@mui/icons-material/FileDownloadOutlined";
import { get } from "aws-amplify/api";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/DeleteOutlined";
import SaveIcon from "@mui/icons-material/Save";
import CancelIcon from "@mui/icons-material/Close";
import {
  DataGrid,
  GridToolbarContainer,
  GridActionsCellItem,
  GridToolbarColumnsButton,
  GridRowModes,
  useGridApiRef,
} from "@mui/x-data-grid";
import { I18n } from "@aws-amplify/core";
import { GridToolbarExport, GridToolbarFilterButton } from "@mui/x-data-grid";
import ConfirmDeleteDialog from "popup/ConfirmDeleteDialog";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import currentUserToken from "CurrentUserToken";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import SearchIcon from "@mui/icons-material/Search";
import { useTheme } from "@mui/material/styles";
import { post } from "aws-amplify/api";
import { APIStatusDialog } from "components/APIStatusDialog";
import EncodeDialog from "popup/EncodeDialog";
import TemplateBox from "components/TemplateBox";
import { useNavigate } from "react-router-dom"; // Import useNavigate for navigation
import { useLocation } from "react-router-dom";
import { DataGridPro } from "@mui/x-data-grid-pro/DataGridPro";
import AddIcon from "@mui/icons-material/Add";
import NewAccountAssignationDialog from "popup/NewAccountAssignationDialog";
import EncodingSettingsMenu from "../components/EncodingSettingsMenu";
import PatternSettingsMenu from "../components/PatternSettingsMenu";
import { itIT } from "@mui/x-data-grid/locales";
import { deDE } from "@mui/x-data-grid/locales";
import EventHistorySettingsMenu from "../components/EventHistorySettingsMenu";
const reducer = (lastState, action) => {
  if (action.type === "DATA_RECEIVED") {
    const audit = action.value;
    if (Array.isArray(audit)) {
      audit.forEach((element) => {
        if (typeof element.timestamp === "number") {
          element.timestamp = dayjs(element.timestamp).format(
            "YYYY-MM-DD HH:mm"
          );
        }
      });
      return { audit: audit, fetched: true };
    }
  }
  return lastState;
};
function AccountAssignation() {
  const [state, dispatch] = useReducer(reducer, {
    audit: [],
    fetched: false,
  });
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedAlarmTypeIdToDelete, setSelectedAlarmTypeIdToDelete] =
    useState(null);
  const [openEncodeDialog, setOpenEncodeDialog] = useState(false);
  const [apiStatus, setApiStatus] = useState({
    open: false,
    status: "",
    message: "",
  });
  const [languages, setLanguages] = useState([]);
  const [tableParentIsMD, setTableParentIsMD] = useState(false);
  const [tabValue, setTabValue] = useState(0);
  const navigate = useNavigate(); // Use navigate to switch between routes
  const location = useLocation();
  const [resizeThrottle, setResizeThrottle] = useState(60);
  const toolbarRef = useRef(null);
  const [openNewAccountAssignationDialog, setOpenNewAccountAssignationDialog] =
    useState(false);
  const handleSaveNewAccountAssignation = (newData) => {};
  const handleTabChange = (event, newValue) => {
    setTabValue(newValue); // Set the active tab
    switch (newValue) {
      case 0: // Alarms List tab (current component)
        navigate("/settings"); // Optional if this is the same route
        break;
      case 1: // Alarms tab (navigates to /profile)
        navigate("/settings/uncoded");
        break;
      case 2: // Alarms tab (navigates to /profile)
        navigate("/settings/assignation");
        break;
      case 3: // Alarms tab (navigates to /profile)
        navigate("/settings/note");
        break;
      case 4: // Alarms tab (navigates to /profile)
        navigate("/settings/close");
        break;
      default:
        break;
    }
  };
  useEffect(() => {
    const currentPath = window.location.pathname;
    if (currentPath.includes("/settings")) {
      setTabValue(0);
    } else if (currentPath.includes("/settings/uncoded")) {
      setTabValue(1);
    } else if (currentPath.includes("/settings/assignation")) {
      setTabValue(2);
    } else if (currentPath.includes("/settings/note")) {
      setTabValue(3);
    } else if (currentPath.includes("/settings/close")) {
      setTabValue(4);
    }
  }, []);

  useEffect(() => {
    if (location.pathname.includes("/settings/close")) {
      setTabValue(4);
    } else if (location.pathname.includes("/settings/note")) {
      setTabValue(3); // Set to 'Unencoded Alarms' tab
    } else if (location.pathname.includes("/settings/assignation")) {
      setTabValue(2); // Set to 'Unencoded Alarms' tab
    } else if (location.pathname.includes("/settings/uncoded")) {
      setTabValue(1); // Set to 'Alarm Types' tab
    } else {
      setTabValue(0);
    }
  }, [location.pathname]); // Run this effect every time the URL changes

  useEffect(() => {
    const parentElement = toolbarRef.current;
    if (!parentElement) {
      console.error("toolbarRef is not correctly attached to any DOM element.");
      return;
    }
    const checkSize = (width) => {
      const breakpoint = 960; // 960px is md
      setTableParentIsMD(width < breakpoint);
    };
    const resizeObserver = new ResizeObserver((entries) => {
      for (let entry of entries) {
        checkSize(entry.contentRect.width);
      }
    });
    resizeObserver.observe(parentElement);
    checkSize(parentElement.offsetWidth);
    return () => {
      resizeObserver.disconnect();
    };
  }, []);
  const apiRef = useGridApiRef();
  const [searchText, setSearchText] = useState("");
  const theme = useTheme();
  const [language, setLanguage] = useState(
    localStorage.getItem("language") || "en"
  );
  const [rowModesModel, setRowModesModel] = useState({});
  const [selectedRows, setSelectedRows] = useState([]);
  const [filters, setFilters] = useState({});
  const [totalCount, setTotalCount] = useState(0);
  const handleRowSelection = (ids) => {
    setSelectedRows(ids);
  };

  useEffect(() => {
    const hiddenColumns = JSON.parse(
      localStorage.getItem("hiddenColumns") || "[]"
    );
    if (apiRef.current) {
      apiRef.current.setColumnVisibilityModel(
        hiddenColumns.reduce((model, field) => {
          model[field] = false;
          return model;
        }, {})
      );
    }
  }, [apiRef]);

  const handleColumnVisibilityChange = (newModel) => {
    const hiddenColumns = Object.keys(newModel).filter(
      (field) => !newModel[field]
    );
    localStorage.setItem("hiddenColumns", JSON.stringify(hiddenColumns));
  };

  const tlmGetAlarmAccountMap = async () => {
    try {
      const restOperation = get({
        apiName: "TelemonitoringAPI",
        path: "/alarm/account/map/get",
        options: {
          headers: {
            "Content-Type": "application/json",
            Authorization: `${await currentUserToken()}`,
          },
        },
      });
      const { body } = await restOperation.response;
      const response = await body.json();

      dispatch({ type: "DATA_RECEIVED", value: response });
    } catch (error) {
      console.error("Errore durante la chiamata API:", error);
    }
  };
  useEffect(() => {
    tlmGetAlarmAccountMap();
  }, []);

  useEffect(() => {
    const tlmGetLanguagesAPI = async () => {
      try {
        const restOperation = get({
          apiName: "TelemonitoringAPI",
          path: "/languages",
          options: {
            headers: {
              "Content-Type": "application/json",
              Authorization: `${await currentUserToken()}`,
            },
            queryParams: {},
          },
        });
        const { body } = await restOperation.response;
        const response = await body.json();

        setLanguages(response);
      } catch (error) {
        console.error("Errore durante la chiamata API:", error);
      }
    };
    tlmGetLanguagesAPI();
  }, []);

  const columns = useMemo(() => {
    const handleCancelClick = (id) => () => {
      setRowModesModel({
        ...rowModesModel,
        [id]: { mode: GridRowModes.View, ignoreModifications: true },
      });
    };
    const handleEditClick = (id) => () => {
      setRowModesModel({ ...rowModesModel, [id]: { mode: GridRowModes.Edit } });
    };
    const handleSaveClick = (id) => () => {
      setRowModesModel({
        ...rowModesModel,
        [id]: { mode: GridRowModes.View },
      });
    };
    const handleDeleteClick = (id) => () => {
      setSelectedAlarmTypeIdToDelete(id); // Memorizza l'ID dell'allarme da eliminare
      setDeleteDialogOpen(true); // Apri il pop-up di conferma
    };

    return [
      {
        field: "account",
        headerName: I18n.get("Account"),
        width: 200,
        editable: true,
      },
      {
        field: "pattern_number",
        headerName: I18n.get("Pattern Number"),
        width: 200,
        editable: true,
      },
      {
        field: "search_pattern_1",
        headerName: I18n.get("Search Pattern 1"),
        width: 200,
        editable: true,
      },
      {
        field: "search_pattern_2",
        headerName: I18n.get("Search Pattern 2"),
        width: 200,
        editable: true,
      },
      {
        field: "search_pattern_3",
        headerName: I18n.get("Search Pattern 3"),
        width: 200,
        editable: true,
      },
      {
        field: "actions",
        type: "actions",
        headerName: I18n.get("Actions"),
        width: 150,
        cellClassName: "actions",
        getActions: ({ id, row }) => {
          const isInEditMode = rowModesModel[id]?.mode === GridRowModes.Edit;
          if (isInEditMode) {
            return [
              <Tooltip title={I18n.get("Save")}>
                <GridActionsCellItem
                  icon={<SaveIcon />}
                  label={I18n.get("Save")}
                  sx={{ color: "primary.main" }}
                  onClick={handleSaveClick(id)}
                />
              </Tooltip>,
              <Tooltip title={I18n.get("Cancel")}>
                <GridActionsCellItem
                  icon={<CancelIcon />}
                  label={I18n.get("Cancel")}
                  onClick={handleCancelClick(id)}
                />
              </Tooltip>,
            ];
          }
          return [
            <Tooltip title={I18n.get("Edit")}>
              <GridActionsCellItem
                icon={<EditIcon />}
                label={I18n.get("Edit")}
                onClick={handleEditClick(id)}
              />
            </Tooltip>,
            <Tooltip title={I18n.get("Delete")}>
              <GridActionsCellItem
                icon={<DeleteIcon />}
                label={I18n.get("Delete")}
                onClick={handleDeleteClick(id)}
                disabled={row.alarm_type_id === 1}
              />
            </Tooltip>,
          ];
        },
      },
    ];
  }, [rowModesModel]);

  const handleRowEditStop = (params, event) => {
    if (params.reason === GridRowModes.rowFocusOut) {
      event.defaultMuiPrevented = true;
    }
  };

  const handleConfirmDelete = async () => {
    setDeleteDialogOpen(false);
    try {
      setApiStatus({
        open: true,
        status: "pending",
        message: I18n.get("Deleting the assignment"),
      });

      const restOperation = await tlmDeletePI(selectedAlarmTypeIdToDelete);
      const response = await restOperation.response;
      if (response.statusCode === 200) {
        setApiStatus({
          open: true,
          status: "success",
          message: I18n.get("Assignment successfully deleted"),
        });
        dispatch({
          type: "DATA_RECEIVED",
          value: state.audit.filter(
            (row) => row.alarm_type_id !== selectedAlarmTypeIdToDelete
          ),
        });

        tlmGetAlarmAccountMap();
      } else {
        setApiStatus({
          open: true,
          status: "error",
          message: I18n.get("Failed to delete assignment"),
        });
      }
      setTimeout(() => {
        setApiStatus((prevState) => ({ ...prevState, open: false }));
      }, 2000);
    } catch (error) {
      setApiStatus({
        open: true,
        status: "error",
        message: I18n.get("Failed to delete assignment"),
      });
      setTimeout(() => {
        setApiStatus((prevState) => ({ ...prevState, open: false }));
      }, 2000);
    }
  };
  const handleCloseDeleteDialog = () => {
    setDeleteDialogOpen(false); // Chiudi il dialogo senza fare nulla
  };

  const processRowUpdate = async (newRow) => {
    const rowToReturn = { ...newRow, isNew: false };

    const originalRow = state.audit.find((row) => row.id === newRow.id);
    const updatedRow = { ...originalRow, ...newRow };
    const { id, ...rowWithoutId } = updatedRow;

    try {
      setApiStatus({
        open: true,
        status: "pending",
        message: I18n.get("Modifying"),
      });

      const restOperation = await tlmModifyDescription(newRow.id, rowWithoutId);
      const response = await restOperation.response;
      if (response.statusCode === 200) {
        setApiStatus({
          open: true,
          status: "success",
          message: I18n.get("Modification successfully saved"),
        });
        dispatch({
          type: "DATA_RECEIVED",
          value: state.audit.map((row) =>
            row.id === newRow.id ? updatedRow : row
          ),
        });
        setTimeout(() => {
          setApiStatus((prevState) => ({ ...prevState, open: false }));
        }, 2000);
        return newRow;
      } else {
        setApiStatus({
          open: true,
          status: "error",
          message: I18n.get("Failed to save modification"),
        });
        setTimeout(() => {
          setApiStatus((prevState) => ({ ...prevState, open: false }));
        }, 2000);
        return newRow;
      }
    } catch (error) {
      setApiStatus({
        open: true,
        status: "error",
        message: I18n.get("Failed to save modification"),
      });
      setTimeout(() => {
        setApiStatus((prevState) => ({ ...prevState, open: false }));
      }, 2000);
      return newRow;
    }
  };

  const tlmDeletePI = async (alarmTypeId) => {
    try {
      const restOperation = get({
        apiName: "TelemonitoringAPI",
        path: "/alarm/account/map/remove",
        options: {
          headers: {
            "Content-Type": "application/json",
            Authorization: `${await currentUserToken()}`,
          },
          queryParams: {
            id: alarmTypeId, // Passa l'ID dell'allarme come query parameter
          },
        },
      });
      return restOperation;
    } catch (error) {
      throw error;
    }
  };
  const tlmModifyDescription = async (alarmTypeId, updatedRow) => {
    try {
      const restOperation = await post({
        apiName: "TelemonitoringAPI",
        path: "/alarm/account/map/edit",
        options: {
          body: updatedRow,
          headers: {
            "Content-Type": "application/json",
            Authorization: `${await currentUserToken()}`,
          },
          queryParams: {
            id: alarmTypeId,
          },
        },
      });
      return restOperation;
    } catch (error) {
      console.error("Error during API call:", error);
    }
  };

  const applyStoreFiltering = (value) => {
    let matchesAllFilters = true;
    if (searchText) {
      const searchTextLower = searchText.toLowerCase();
      matchesAllFilters =
        matchesAllFilters &&
        Object.values(value).some((val) =>
          String(val).toLowerCase().includes(searchTextLower)
        );
    }
    return matchesAllFilters;
  };
  const filteredRows = state.audit.filter(applyStoreFiltering);
  const filteredRowsCount = filteredRows.length;
  const searchChanged = (event) => {
    setSearchText(event.target.value);
  };
  const renderToolbar = () => {
    return (
      <div ref={toolbarRef}>
        <Stack direction="row" alignItems="center" spacing={2} mb={1}>
          <Typography variant="h5" mt={0.5} mb={1}>
            {I18n.get("Account Assignment")} (
            {filters ? filteredRowsCount : totalCount})
          </Typography>
        </Stack>
        <Stack
          direction={tableParentIsMD ? "column" : "row"}
          alignItems="center"
          justifyContent={tableParentIsMD ? "center" : "space-between"}
          spacing={2}
          sx={{ pb: 2 }}
        >
          <Stack
            direction={tableParentIsMD ? "column" : "row"}
            spacing={2}
            sx={{ width: "100%" }}
          >
            <Button
              onClick={() => setOpenNewAccountAssignationDialog(true)} // Open the pop-up
              color="primary"
              variant="outlined"
              startIcon={<AddIcon />}
              fullWidth={tableParentIsMD}
            >
              {I18n.get("New Account Assignment")}
            </Button>
          </Stack>
          <Input
            type="text"
            placeholder={I18n.get("Search")}
            onChange={searchChanged}
            fullWidth={tableParentIsMD}
            endAdornment={
              <InputAdornment position="end">
                <IconButton>
                  <SearchIcon />
                </IconButton>
              </InputAdornment>
            }
          />
        </Stack>
      </div>
    );
  };
  
  const [tableHeight, setTableHeight] = useState(0);
  useEffect(() => {
    const updateHeight = () => {
      const navbarHeight = document.getElementById("navbar");
      const footerHeight = document.getElementById("footer");
      const margin =
        browser === "Firefox" ? 200 : browser === "Chrome" ? 200 : 200;
      const availableHeight =
        document.documentElement.clientHeight -
        navbarHeight -
        footerHeight -
        margin;
      setTableHeight(availableHeight);
    };

    updateHeight();
    window.addEventListener("resize", updateHeight);
    return () => window.removeEventListener("resize", updateHeight);
  }, []);
  useEffect(() => {
    document.body.style.overflow = "hidden";
  }, []);
  function getBrowserName() {
    const userAgent = navigator.userAgent;
    if (userAgent.includes("Firefox")) {
      return "Firefox";
    } else if (userAgent.includes("Edg")) {
      return "Edge";
    } else if (userAgent.includes("Chrome")) {
      return "Chrome";
    }
  }
  const browser = getBrowserName();
  const darkMode=localStorage.getItem("darkMode")==="true";
  return (
    <>
      <DashboardLayout>
        <DashboardNavbar
          absolute
          light
          language={language}
          setLanguage={setLanguage}
        />
        <Stack
          direction="row"
          spacing={1}
          sx={{ ml: 3, mt: 3, width: "400px" }}
        >
          <EncodingSettingsMenu />
          <PatternSettingsMenu />
          <EventHistorySettingsMenu />
        </Stack>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <Box sx={{ p: 2, maxHeight: `${tableHeight}px` }}>
              {" "}
              {/** lasciare così */}
              <Card
                sx={{
                  p: 3,
                  maxHeight: `${tableHeight}px`,
                }}
              >
                {renderToolbar()}
                <Box
                  sx={{ p: 2, height: `${tableHeight}px`, overflow: "auto" }}
                >
                  <DataGridPro
                    sx={{
                      "& .MuiDataGrid-root, & .MuiDataGrid-footerContainer": {
                        minHeight: "2rem",
                      },
                      "&  .MuiDataGrid-cell ": {
                        cursor: "default",
                      },
                      borderColor: darkMode? "#6B635A":"",
                      "& .MuiDataGrid-cell, &.MuiDataGrid-columnHeaders": {
                        borderColor: darkMode?"#625a52":"",
                      },
                      "& .MuiDataGrid-columnSeparator": {
                        borderColor: darkMode?"#625a52":"",
                      },
                      "--DataGrid-rowBorderColor": darkMode?"#625a52":"",
                      minHeight: "20vh",
                      flexFlow: 1,
                      height:"100%"
                    }}
                    localeText={
                      language === "it"
                        ? itIT.components.MuiDataGrid.defaultProps.localeText
                        : language === "ge"
                        ? deDE.components.MuiDataGrid.defaultProps.localeText
                        : ""
                    }
                    onCellDoubleClick={(params, event) => {
                      // Prevent the default double-click behavior
                      event.defaultMuiPrevented = true;
                    }}
                    editMode="row"
                    rows={filteredRows}
                    columns={columns}
                    disableRowSelectionOnClick
                    getRowId={(row) => row.id}
                    rowModesModel={rowModesModel}
                    pagination
                    onRowModesModelChange={(newModel) =>
                      setRowModesModel(newModel)
                    }
                    onRowSelectionModelChange={(newSelection) =>
                      handleRowSelection(newSelection)
                    }
                    initialState={{
                      density: "compact",
                      pinnedColumns: { right: ["actions"] },
                    }}
                    apiRef={apiRef}
                     resizeThrottleMs={resizeThrottle}
                     loading={!state.fetched || resizeThrottle > 60}
                    onRowEditStop={handleRowEditStop}
                    processRowUpdate={(newRow) => processRowUpdate(newRow)}
                    onProcessRowUpdateError={(error) => console.log({ error })}
                    onColumnVisibilityModelChange={handleColumnVisibilityChange}
                    onSelectionModelChange={(newSelection) => {
                      const selectedRows = newSelection.map((id) =>
                        filteredRows.find((row) => row.id === id)
                      );
                    }}
                    hideFooterRowCount //rimuove total row count
                    autoPageSize
                    slotProps={{
                      loadingOverlay: {
                        variant: "skeleton",
                        noRowsVariant: "skeleton",
                      },
                    }}
                    slots={{
                      toolbar: () => (
                        <GridToolbarContainer>
                          <GridToolbarColumnsButton />
                          <GridToolbarExport
                            printOptions={{ disableToolbarButton: true }}
                            csvOptions={{
                              fileName: "Account Assignments",
                              utf8WithBom: true,
                            }}
                          />
                          <GridToolbarFilterButton />
                        </GridToolbarContainer>
                      ),
                    }}
                  />
                </Box>
              </Card>
            </Box>
          </Grid>
        </Grid>
      </DashboardLayout>
      <APIStatusDialog
        open={apiStatus.open}
        status={apiStatus.status}
        message={apiStatus.message}
      />
      <ConfirmDeleteDialog
        parent={"account_assignment"}
        open={deleteDialogOpen}
        onClose={handleCloseDeleteDialog}
        onConfirm={handleConfirmDelete}
      />
      <NewAccountAssignationDialog
        open={openNewAccountAssignationDialog}
        onClose={() => {
          setOpenNewAccountAssignationDialog(false);
          tlmGetAlarmAccountMap(); // Call to refresh the data
        }}
        onSave={handleSaveNewAccountAssignation}
      />
    </>
  );
}

export default AccountAssignation;
